import React from 'lib/react';
import PropTypes from 'prop-types';
import {
  EVGrid, makeStyles, EVRadio,
} from 'lib/components';
import clsx from 'lib/clsx';
import { useTranslation } from 'lib/translation';
import { useSelector, useDispatch } from 'lib/redux';
import { updateInputFieldValue } from 'features/orderPreferences/OrderPreferences.actions';
import { getPropertyValues } from 'features/orderPreferences/OrderPreferences.utils';
import { PRODUCT_SUBSTITUTIONS_OPTIONS } from '../ProductSubstitutions.constants';

const useStyles = makeStyles((theme) => ({
  optionsContainer: {
    display: 'flex',
    maxHeight: '42px',
    margin: 'auto 0px',
  },
  optionLabel: {
    display: 'flex',
    border: `1px solid ${theme.evPalette.ocean[600]}`,
    padding: '10px 16px',
    // set border for all sides except right for all options except the last one
    borderWidth: '1px 0px 1px 1px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    lineHeight: '19.07px',
    alignItems: 'center',
    color: theme.evPalette.ocean[600],
    '&.yes': {
      borderRadius: '4px 0px 0px 4px',
    },
    '&.ask': {
      // set border for all sides for the last option
      borderWidth: '1px',
      borderRadius: '0px 4px 4px 0px',
    },
    '&.selected': {
      color: theme.evPalette.salt[100],
      backgroundColor: theme.evPalette.ocean[600],
    },
    '&.disabled': {
      borderColor: theme.evPalette.charcoal[500],
      color: theme.evPalette.charcoal[500],
    },
    '&.disabled.selected': {
      backgroundColor: theme.evPalette.charcoal[500],
      color: theme.evPalette.salt[100],
    },
  },
  disabledContainer: {
    backgroundColor: theme.evPalette.charcoal[500],
  },
  radioInput: {
    padding: 0,
  },
}));

function ProductSubstitutionOptions(props) {
  const { propertyName } = props;
  const { t } = useTranslation();

  const inputFieldValues = useSelector(({ orderPreference }) => orderPreference.inputFieldValues);
  const orderPreferences = useSelector(({ userData }) => userData.orderPreferences);
  const { value: selectedValue } = getPropertyValues(orderPreferences, inputFieldValues, propertyName);
  const classes = useStyles({ value: selectedValue });

  const dispatch = useDispatch();
  const handleChange = ({ target: { value } }) => dispatch(updateInputFieldValue(propertyName, 'value', Number(value)));

  const disabled = !orderPreferences[propertyName].canEdit;

  const getOptionIcon = (option, isChecked) => (
    <EVGrid className={clsx(classes.optionLabel, option, isChecked && 'selected', disabled && 'disabled')}>
      {t(`common_${option}`)}
    </EVGrid>
  );

  return (
    <EVGrid className={classes.optionsContainer}>
      {Object.entries(PRODUCT_SUBSTITUTIONS_OPTIONS).map(([optionKey, optionValue]) => (
        <EVRadio
          key={optionKey}
          id={`${optionKey.toLowerCase()}-${propertyName}`}
          value={optionValue}
          name={propertyName}
          checked={selectedValue === optionValue}
          disabled={disabled}
          onChange={handleChange}
          className={classes.radioInput}
          icon={getOptionIcon(optionKey.toLowerCase(), false)}
          checkedIcon={getOptionIcon(optionKey.toLowerCase(), true)}
        />
      ))}
    </EVGrid>
  );
}
ProductSubstitutionOptions.propTypes = {
  propertyName: PropTypes.string.isRequired,
};

export default ProductSubstitutionOptions;
